import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getAllUsers, createBillForUser, createBillForAllResidents, getBills, getTenants, getOutstandingDuesRemovedUsers, writeOffDue } from '@/services/api';
import { Plus, IndianRupee, Users, Search, FileText, CheckCircle2, Clock, AlertCircle, Calendar, Home, User, UserX, AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';

const AdminBills = () => {
  const [residents, setResidents] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [outstandingDues, setOutstandingDues] = useState(null);
  const [writeOffNote, setWriteOffNote] = useState('');
  const [writingOff, setWritingOff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    due_date: '',
    description: '',
    bill_type: 'maintenance'
  });
  const [bulkFormData, setBulkFormData] = useState({
    amount: '',
    due_date: '',
    description: '',
    bill_type: 'maintenance'
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...bills];
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        b.user_name?.toLowerCase().includes(term) ||
        b.unit_number?.toLowerCase().includes(term) ||
        b.description?.toLowerCase().includes(term) ||
        b.bill_type?.toLowerCase().includes(term)
      );
    }
    
    setFilteredBills(filtered);
  }, [bills, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      const [usersData, billsData, tenantsData, duesData] = await Promise.all([
        getAllUsers(),
        getBills(),
        getTenants().catch(() => []),
        getOutstandingDuesRemovedUsers().catch(() => null)
      ]);
      const activeResidents = usersData.filter(u => u.role === 'resident' && (u.status === 'active' || !u.status));
      const activeTenants = tenantsData.filter(t => t.role === 'tenant' || t.status === 'active');
      setResidents(activeResidents);
      setTenants(activeTenants);
      setAllUsers([...activeResidents, ...activeTenants]);
      setBills(billsData);
      setFilteredBills(billsData);
      setOutstandingDues(duesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleBill = async (e) => {
    e.preventDefault();
    try {
      await createBillForUser({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Bill created successfully');
      setDialogOpen(false);
      setFormData({ user_id: '', amount: '', due_date: '', description: '', bill_type: 'maintenance' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bill');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
      case 'pending':
      case 'unpaid':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Unpaid</Badge>;
      case 'partial':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Partial</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getBillTypeIcon = (billType) => {
    switch (billType?.toLowerCase()) {
      case 'amenity':
      case 'amenity_booking':
        return <FileText className="h-4 w-4 text-blue-600" />;
      case 'maintenance':
        return <Home className="h-4 w-4 text-green-600" />;
      case 'utility':
        return <AlertCircle className="h-4 w-4 text-amber-600" />;
      default:
        return <FileText className="h-4 w-4 text-slate-600" />;
    }
  };

  // Calculate totals
  const totalAmount = bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const pendingAmount = bills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);
  const paidAmount = bills.filter(b => b.status === 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);

  const handleBulkBills = async (e) => {
    e.preventDefault();
    try {
      const result = await createBillForAllResidents(bulkFormData);
      toast.success(result.message);
      setBulkFormData({ amount: '', due_date: '', description: '', bill_type: 'maintenance' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create bills');
    }
  };

  const handleWriteOff = async (dueType, dueId) => {
    if (!writeOffNote.trim()) {
      toast.error('Write-off note is required');
      return;
    }
    try {
      setWritingOff(dueId);
      await writeOffDue(dueType, dueId, writeOffNote.trim());
      toast.success('Due written off successfully');
      setWriteOffNote('');
      setWritingOff(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to write off');
    } finally {
      setWritingOff(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-bills">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Bills Management</h1>
          <p className="text-sm md:text-base text-slate-600">Create and manage resident bills</p>
        </div>
      </div>

      {/* Create Bills Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Create Bills</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="single">Single Resident</TabsTrigger>
              <TabsTrigger value="bulk">All Residents</TabsTrigger>
            </TabsList>
            
            <TabsContent value="single">
              <form onSubmit={handleSingleBill} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="resident">Select User (Resident/Tenant)</Label>
                  <Select value={formData.user_id} onValueChange={(value) => setFormData({ ...formData, user_id: value })}>
                    <SelectTrigger data-testid="bill-resident-select">
                      <SelectValue placeholder="Choose a user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel className="font-semibold text-slate-700">Residents</SelectLabel>
                        {residents.map((resident) => (
                          <SelectItem key={resident.id} value={resident.id}>
                            {resident.name} - {resident.unit_number || 'N/A'} (Resident)
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      {tenants.length > 0 && (
                        <>
                          <SelectSeparator />
                          <SelectGroup>
                            <SelectLabel className="font-semibold text-slate-700">Tenants</SelectLabel>
                            {tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>
                                {tenant.name} - {tenant.unit_number || 'N/A'} (Tenant)
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bill-type">Bill Type</Label>
                  <Select value={formData.bill_type} onValueChange={(value) => setFormData({ ...formData, bill_type: value })}>
                    <SelectTrigger data-testid="bill-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="utility">Utility</SelectItem>
                      <SelectItem value="amenity">Amenity</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (₹)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="1500.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      data-testid="bill-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due-date">Due Date</Label>
                    <Input
                      id="due-date"
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      data-testid="bill-due-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Monthly Maintenance - March 2026"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    data-testid="bill-description-input"
                    rows={3}
                  />
                </div>
                <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" data-testid="create-single-bill-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Bill
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="bulk">
              <form onSubmit={handleBulkBills} className="space-y-4 mt-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Note:</strong> This will create bills for all active occupants in the society. If a unit has a tenant, the bill goes to the tenant instead of the resident (landlord).
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-bill-type">Bill Type</Label>
                  <Select value={bulkFormData.bill_type} onValueChange={(value) => setBulkFormData({ ...bulkFormData, bill_type: value })}>
                    <SelectTrigger data-testid="bulk-bill-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="utility">Utility</SelectItem>
                      <SelectItem value="amenity">Amenity</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bulk-amount">Amount (₹)</Label>
                    <Input
                      id="bulk-amount"
                      type="number"
                      step="0.01"
                      placeholder="1500.00"
                      value={bulkFormData.amount}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, amount: e.target.value })}
                      required
                      data-testid="bulk-bill-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bulk-due-date">Due Date</Label>
                    <Input
                      id="bulk-due-date"
                      type="date"
                      value={bulkFormData.due_date}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, due_date: e.target.value })}
                      required
                      data-testid="bulk-bill-due-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-description">Description</Label>
                  <Textarea
                    id="bulk-description"
                    placeholder="Monthly Maintenance - March 2026"
                    value={bulkFormData.description}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, description: e.target.value })}
                    required
                    data-testid="bulk-bill-description-input"
                    rows={3}
                  />
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="create-bulk-bills-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Create Bills for All Residents & Tenants
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Bills Summary */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Bills Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total Bills</p>
              <p className="text-2xl font-bold text-slate-900">{bills.length}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Total Amount</p>
              <p className="text-xl font-bold text-blue-700 flex items-center gap-1">
                <IndianRupee className="h-4 w-4" />
                {totalAmount.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Pending</p>
              <p className="text-xl font-bold text-red-700 flex items-center gap-1">
                <IndianRupee className="h-4 w-4" />
                {pendingAmount.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Collected</p>
              <p className="text-xl font-bold text-green-700 flex items-center gap-1">
                <IndianRupee className="h-4 w-4" />
                {paidAmount.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* All Bills List */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>All Bills</CardTitle>
              <CardDescription>View and manage all user bills</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, unit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-full sm:w-[200px]"
                  data-testid="bills-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="bills-status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredBills.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No bills found</p>
              <p className="text-sm">Create bills using the form above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBills.map((bill) => (
                <div 
                  key={bill.id} 
                  className={`p-4 rounded-xl border ${
                    bill.status === 'paid' ? 'bg-green-50 border-green-200' : 
                    bill.status === 'pending' ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid={`bill-row-${bill.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        bill.status === 'paid' ? 'bg-green-100' : 
                        bill.status === 'pending' ? 'bg-red-100' : 'bg-slate-100'
                      }`}>
                        {bill.status === 'paid' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900">{bill.user_name || 'Unknown User'}</p>
                          {bill.unit_number && (
                            <Badge variant="outline" className="text-xs">
                              <Home className="h-3 w-3 mr-1" />
                              {bill.unit_number}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">
                            {getBillTypeIcon(bill.bill_type)}
                            <span className="ml-1">{bill.bill_type || 'maintenance'}</span>
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{bill.description || bill.title || 'No description'}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due: {bill.due_date ? new Date(bill.due_date).toLocaleDateString('en-IN') : 'N/A'}
                          </span>
                          {bill.paid_date && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="h-3 w-3" />
                              Paid: {new Date(bill.paid_date).toLocaleDateString('en-IN')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-10 md:ml-0">
                      <div className="text-right">
                        <p className="font-bold text-lg text-slate-900 flex items-center gap-1">
                          <IndianRupee className="h-4 w-4" />
                          {bill.amount?.toLocaleString('en-IN')}
                        </p>
                        {bill.paid_amount > 0 && bill.paid_amount < bill.amount && (
                          <p className="text-xs text-green-600">
                            Paid: ₹{bill.paid_amount?.toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(bill.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Outstanding Dues from Removed Users */}
      {outstandingDues && (outstandingDues.unpaid_bills?.length > 0 || outstandingDues.unpaid_rent?.length > 0) && (
        <Card className="border-red-300 shadow-sm bg-red-50/30" data-testid="outstanding-dues-section">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <UserX className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-red-900">Outstanding Dues — Removed Users</CardTitle>
                <CardDescription className="text-red-700">
                  These users are no longer in the society but have pending amounts due
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Banner */}
            <div className="p-4 bg-red-100 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-700" />
                <span className="font-semibold text-red-900">
                  {outstandingDues.summary.total_removed_users_with_dues} removed user(s) with pending dues
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-red-700">Total Outstanding</p>
                <p className="text-xl font-bold text-red-900 flex items-center gap-1 justify-end">
                  <IndianRupee className="h-4 w-4" />
                  {outstandingDues.summary.total_outstanding?.toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            {/* Unpaid Bills */}
            {outstandingDues.unpaid_bills?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-red-800 uppercase tracking-wide">Unpaid Bills</h3>
                {outstandingDues.unpaid_bills.map((bill) => (
                  <div
                    key={bill.id}
                    className="p-4 rounded-xl border border-red-200 bg-white"
                    data-testid={`removed-user-bill-${bill.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-red-50">
                          <UserX className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">{bill.user_name}</p>
                            {bill.user_unit && (
                              <Badge variant="outline" className="text-xs">
                                <Home className="h-3 w-3 mr-1" />
                                {bill.user_unit}
                              </Badge>
                            )}
                            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                              Removed from Society
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{bill.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Due: {bill.due_date ? new Date(bill.due_date).toLocaleDateString('en-IN') : 'N/A'}
                            </span>
                            {bill.user_removed_at && (
                              <span className="text-red-600">
                                Removed: {new Date(bill.user_removed_at).toLocaleDateString('en-IN')}
                              </span>
                            )}
                          </div>
                          {bill.user_removal_reason && (
                            <p className="text-xs text-red-600 mt-1 italic">Reason: {bill.user_removal_reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 ml-10 md:ml-0">
                        <div className="text-right">
                          <p className="font-bold text-lg text-red-800 flex items-center gap-1">
                            <IndianRupee className="h-4 w-4" />
                            {bill.amount?.toLocaleString('en-IN')}
                          </p>
                          <Badge className="bg-red-100 text-red-700 border-red-200">Due</Badge>
                        </div>
                        {writingOff === bill.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Write-off note..."
                              value={writeOffNote}
                              onChange={(e) => setWriteOffNote(e.target.value)}
                              className="w-40 h-8 text-xs"
                              data-testid={`writeoff-note-bill-${bill.id}`}
                            />
                            <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => handleWriteOff('bill', bill.id)} data-testid={`confirm-writeoff-bill-${bill.id}`}>
                              Confirm
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setWritingOff(null); setWriteOffNote(''); }}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="text-xs text-slate-600 border-slate-300 h-7" onClick={() => setWritingOff(bill.id)} data-testid={`writeoff-btn-bill-${bill.id}`}>
                            <Ban className="h-3 w-3 mr-1" /> Write Off
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Unpaid Rent */}
            {outstandingDues.unpaid_rent?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-red-800 uppercase tracking-wide">Unpaid Rent</h3>
                {outstandingDues.unpaid_rent.map((rent, idx) => (
                  <div
                    key={rent.id || idx}
                    className="p-4 rounded-xl border border-red-200 bg-white"
                    data-testid={`removed-user-rent-${rent.id || idx}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-red-50">
                          <UserX className="h-5 w-5 text-red-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-slate-900">{rent.tenant_name}</p>
                            {rent.tenant_unit && (
                              <Badge variant="outline" className="text-xs">
                                <Home className="h-3 w-3 mr-1" />
                                {rent.tenant_unit}
                              </Badge>
                            )}
                            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                              Removed from Society
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">Rent for {rent.month || 'N/A'}</p>
                          {rent.tenant_removed_at && (
                            <p className="text-xs text-red-600 mt-1">
                              Removed: {new Date(rent.tenant_removed_at).toLocaleDateString('en-IN')}
                            </p>
                          )}
                          {rent.tenant_removal_reason && (
                            <p className="text-xs text-red-600 italic">Reason: {rent.tenant_removal_reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 ml-10 md:ml-0">
                        <div className="text-right">
                          <p className="font-bold text-lg text-red-800 flex items-center gap-1">
                            <IndianRupee className="h-4 w-4" />
                            {rent.amount?.toLocaleString('en-IN')}
                          </p>
                          <Badge className="bg-red-100 text-red-700 border-red-200">Due</Badge>
                        </div>
                        {writingOff === (rent.id || `rent-${idx}`) ? (
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Write-off note..."
                              value={writeOffNote}
                              onChange={(e) => setWriteOffNote(e.target.value)}
                              className="w-40 h-8 text-xs"
                              data-testid={`writeoff-note-rent-${rent.id || idx}`}
                            />
                            <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => handleWriteOff('rent', rent.id)} data-testid={`confirm-writeoff-rent-${rent.id || idx}`}>
                              Confirm
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setWritingOff(null); setWriteOffNote(''); }}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="text-xs text-slate-600 border-slate-300 h-7" onClick={() => setWritingOff(rent.id || `rent-${idx}`)} data-testid={`writeoff-btn-rent-${rent.id || idx}`}>
                            <Ban className="h-3 w-3 mr-1" /> Write Off
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBills;